import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApiClient } from "../../contexts/ApiClientContext";
import APIEndPoints from "../../utils/APIEndPoints";
import Button from "../common/button/Button";
import ButtonAction from "../common/button/ButtonAction";
import SETTINGS from "../../globals";
import AppSections from "../../utils/AppSections";
import AppSubSections from "../../utils/AppSubSections";
import ButtonInfo from "../common/button/ButtonInfo";
import AppIcons from "../../utils/AppIcons";
import Loading from "../common/Loading";
import ButtonType from "../common/button/ButtonType";
import ImgUtil from "../../utils/ImgUtil";
import Texture from "../../model/Texture";
import { useAlerts } from "../../contexts/AlertsContext";
import TimeUtil from "../../utils/TimeUtil";
import DownloadCode from "../../model/DownloadCode";
import AlertMessageType from "../common/alert_message/AlertMessageType";
import { IApiClientError } from "../../shared/ApiClient";
import DialogWindowType from "../common/dialog_window/DialogWindowType";
import EmbeddingToolInfo from "../../utils/EmbeddingToolInfo";
import { useAuth } from "../../contexts/AuthContext";
import TextureFileType from "../../model/enums/TextureFileType";
import ReportButton from "../common/ReportButton";

function TextureDetails() {
  // read the ID from the url
  const { id } = useParams();

  const authManager = useAuth();
  // manage loading animation
  const [loading, setLoading] = useState(true);
  // set the data
  const [data, setData] = useState<Texture>(Texture.EmptyInstance());
  // reference to the ApiClient instance
  const apiClient = useApiClient();
  // get a reference to navigation component
  const navigate = useNavigate();
  // reference to the alerts system
  const alertsManager = useAlerts();

  // load record data from the server
  useEffect(() => {
    window.scrollTo(0, 0);

    const getAllData = async () => {
      try {
        let texture: Texture | undefined = undefined;
        if (id) {
          texture = await apiClient?.get<Texture>({
            url: APIEndPoints.TEXTURES.ONE(parseInt(id)),
          });
          setData(texture);
        }
      } catch (error: any) {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Something went wrong. Try again later!",
          });
        }
      }
      setLoading(false);
    };
    getAllData();
  }, [apiClient, id, alertsManager]);

  const getDownloadCode = async (id: number | undefined) => {
    if (!id) {
      return;
    }
    const responseData = await apiClient?.get<DownloadCode>({
      url: APIEndPoints.DOWNLOADS.CODE(id),
      requiresToken: true,
    });
    window.location.href = APIEndPoints.DOWNLOADS.FILE(
      responseData.download_code,
    );
  };

  const handleDelete = () => {
    apiClient
      ?.delete({
        url: APIEndPoints.TEXTURES.ONE(parseInt(id!)),
      })
      .then((response) => {
        alertsManager.showAlertMessage({
          type: AlertMessageType.SUCCESS,
          message: "Record deleted successfully",
        });
        navigate(
          SETTINGS.get_path({
            section: AppSections._3DINSTANCES,
            id: data.id_3Dinstance,
            subsection: AppSubSections.VIEW,
          }),
        );
      })
      .catch((error: IApiClientError) => {
        if (!error.processed) {
          alertsManager.showAlertMessage({
            type: AlertMessageType.ERROR,
            message: "Unable to delete the record",
          });
        }
      });
  };

  if (loading) {
    return <Loading />;
  }

  const canEdit =
    !EmbeddingToolInfo.isChemiTool() &&
    (data.id_user === authManager.getUserID() ||
      authManager.isAdmin());

  return (
    <div className="details-page">
      <div className="details-header">
        {/* Image */}
        <div className="details-icon">
          <div>
            <img
              src={
                ImgUtil.display_icon(data.icon_path) ||
                process.env.REACT_APP_PUBLIC_URL +
                  "/resources/images/no-image.png"
              }
              alt="e3D icon"
            />
          </div>
        </div>

        {/* Info Section */}
        <div className="details-header-content">
          <div className="details-title">
            <h1>{data.name}</h1>
          </div>
          <div className="details-overview">
            <ButtonInfo className="span-3">
              {AppIcons.USER}
              created by: <span>{data.username}</span>
            </ButtonInfo>
            <ButtonInfo className="span-3">
              {AppIcons.GUID} GUID: <span>{data.GUID}</span>
            </ButtonInfo>
            <ButtonInfo>
              {AppIcons.CLOCK}
              created on: <span>{TimeUtil.display_date(data.created_on)}</span>
            </ButtonInfo>
            <div></div>
            <div></div>
            <ButtonInfo>
              {AppIcons.TEXTURES}
              type: <span>{data.type}</span>
            </ButtonInfo>
          </div>
          <div className="flex items-start mt-5">
            {canEdit && (
              <Button
                label="Delete this record"
                purpose={ButtonAction.DELETE}
                onClick={() => {
                  alertsManager.showDialogWindow({
                    type: DialogWindowType.CONFIRM,
                    message: "Are you sure you want to delete this record?",
                    cancelLabel: "Cancel",
                    confirmLabel: "Yes, delete record",
                    onConfirm: handleDelete,
                  });
                }}
              />
            )}

              <ReportButton
                  entityType="Texture"
                  entityName={data.name}
                  entityId={data.id || 0}
              />
          </div>
        </div>
      </div>
      {/* Details Section */}
      <div className="details-section">
        <div className="details-section-title">
          <h2>Texture details</h2>
          {canEdit && (
            <Button
              label="Edit information"
              purpose={ButtonAction.EDIT}
              type={ButtonType.ICON_TEXT}
              onClick={() =>
                navigate(
                  SETTINGS.get_path({
                    section: AppSections.TEXTURES,
                    id: data.id,
                    subsection: AppSubSections.EDIT,
                  }),
                )
              }
            />
          )}
        </div>
        <p className="details-description">{data.description}</p>

        <div className="details-section-content">
          <table className="details-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>GUID</td>
                <td>{data.GUID ?? "Not found"}</td>
              </tr>
              <tr>
                <td>Texture type</td>
                <td>{data.type ?? "Not found"}</td>
              </tr>
              <tr>
                <td>Texture meaning</td>
                <td>
                  {data.textures && data.textures.length > 0
                    ? data.textures[0].texture_type.replace(/_/g, " ")
                    : TextureFileType.OTHER}
                </td>
              </tr>
              <tr>
                <td>License type</td>
                <td>{data.license_type ?? "Not found"}</td>
              </tr>
              <tr>
                <td>Sensor configuration</td>
                <td>{data.sensor_configuration ?? "Not found"}</td>
              </tr>
              {data.id_gltf_file && (
                <tr>
                  <td>GLTF file</td>
                  <td>
                    <a
                      className="link-download"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        getDownloadCode(data.id_gltf_file);
                      }}
                    >
                      {data.gltf_name ?? "click here to download file"}
                    </a>
                  </td>
                </tr>
              )}
              <tr>
                <td>Image files</td>
                <td>
                  {Array.isArray(data.textures) &&
                    data.textures.map((texture) => {
                      return (
                        <a
                          className="link-download block mt-2 mb-2"
                          key={texture.id}
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            getDownloadCode(texture.id);
                          }}
                          download={texture.name}
                        >
                          {texture.name}
                        </a>
                      );
                    })}
                  {!Array.isArray(data.textures) ||
                    (data.textures.length === 0 && <>no files found</>)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default TextureDetails;
